import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { services, whyChooseUs, industries, processSteps, cloudCostFeatures, cloudCostStats, portfolio } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getIcon = (iconName) => {
  const Icon = LucideIcons[iconName];
  return Icon ? <Icon /> : <LucideIcons.Box />;
};

const getGradientColor = (index) => {
  const gradients = [
    'from-purple-500 to-pink-600',
    'from-blue-500 to-cyan-500',
    'from-green-500 to-emerald-500',
    'from-orange-500 to-red-500',
    'from-indigo-500 to-purple-500',
    'from-teal-500 to-green-500',
    'from-rose-500 to-pink-500',
    'from-cyan-500 to-blue-500'
  ];
  return gradients[index % gradients.length];
};

export const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    description: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const openContact = () => setContactOpen(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.status === 201) {
        toast.success('Thank you! We will contact you soon.');
        setFormData({ name: '', company: '', email: '', phone: '', service: '', description: '' });
        setContactOpen(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1e3a8a] via-[#0F172A] to-[#0a0e1a] relative">
      {/* Premium Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Animated Gradient Mesh */}
        <div className="absolute inset-0 bg-gradient-radial from-[#2563EB]/10 via-transparent to-transparent opacity-50 animate-pulse-slow"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{backgroundImage: 'linear-gradient(rgba(56, 189, 248, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(56, 189, 248, 0.5) 1px, transparent 1px)', backgroundSize: '100px 100px'}}></div>
        
        {/* Noise Texture */}
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}}></div>
        
        {/* Radial Glows */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#2563EB]/10 rounded-full blur-[100px] animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#38BDF8]/10 rounded-full blur-[100px] animate-float" style={{animationDelay: '2s'}}></div>
      </div>
      
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-transparent/95 backdrop-blur-lg border-b border-gray-700">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <img src="/zencodex-logo-symbol.png" alt="ZenCodex" className="h-10 sm:h-16 md:h-20" />
              <img src="/zencodex-text-logo.png" alt="ZenCodex Technologies" className="h-8 sm:h-12 md:h-16" />
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-gray-300 hover:text-[#38BDF8] transition-colors font-medium">Services</a>
              <a href="#industries" className="text-gray-300 hover:text-[#38BDF8] transition-colors font-medium">Industries</a>
              <a href="#portfolio" className="text-gray-300 hover:text-[#38BDF8] transition-colors font-medium">Portfolio</a>
              <button type="button" onClick={openContact} className="text-gray-300 hover:text-[#38BDF8] transition-colors font-medium">Contact</button>
              <Button type="button" onClick={openContact} className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white">Book Free Consultation</Button>
            </div>
            <button className="md:hidden text-white">
              <LucideIcons.Menu className="h-6 w-6" />
            </button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="min-h-screen pt-32 pb-32 px-6 bg-transparent text-white flex items-center">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6 animate-fade-in">
              <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                Build Faster. Scale Smarter. Spend Less on Cloud. <span className="text-[#38BDF8]"></span> 
              </h1>
              <p className="text-xl md:text-2xl text-gray-300">
                We help businesses build scalable applications, optimize cloud costs, modernize infrastructure, and accelerate digital transformation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button type="button" size="lg" onClick={openContact} className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg">
                  <LucideIcons.Calendar className="mr-2 h-5 w-5" />
                  Book Free Consultation
                </Button>
                <Button type="button" size="lg" variant="outline" onClick={() => scrollToSection('services')} className="border-2 border-white text-white hover:bg-white hover:text-[#0F172A] px-8 py-6 text-lg">
                  View Our Services
                  <LucideIcons.ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4 sm:flex sm:items-center sm:gap-8 pt-6 sm:pt-8">
                <div className="text-center sm:text-left">
                  <div className="text-2xl sm:text-3xl font-bold text-[#38BDF8]">150+</div>
                  <div className="text-xs sm:text-base text-gray-400">Projects Delivered</div>
                </div>
                <div className="text-center sm:text-left">
                  <div className="text-2xl sm:text-3xl font-bold text-[#38BDF8]">98%</div>
                  <div className="text-xs sm:text-base text-gray-400">Client Satisfaction</div>
                </div>
                <div className="text-center sm:text-left">
                  <div className="text-2xl sm:text-3xl font-bold text-[#38BDF8]">50+</div>
                  <div className="text-xs sm:text-base text-gray-400">Enterprise Clients</div>
                </div>
              </div>
            </div>
            <div className="relative hidden md:block">
              <div className="absolute inset-0 bg-gradient-to-r from-[#2563EB] to-[#38BDF8] opacity-20 blur-3xl rounded-full"></div>
              <div className="relative flex items-center justify-center scale-125">
                {/* Expanded Hexagon Grid Layout - 8 hexagons */}
                <div className="hexagon-container-large">
                  {/* Row 1 - 2 hexagons */}
                  <div className="hexagon-row">
                    <div className="hexagon-large" style={{animationDelay: '0.1s'}}>
                      <img src="/hero-1.png" alt="Code Development" />
                    </div>
                    <div className="hexagon-large" style={{animationDelay: '0.2s'}}>
                      <img src="/hero-2.png" alt="AI Automation" />
                    </div>
                  </div>
                  {/* Row 2 - 3 hexagons */}
                  <div className="hexagon-row hexagon-row-offset">
                    <div className="hexagon-large" style={{animationDelay: '0.3s'}}>
                      <img src="/hero-3.png" alt="Mobile Development" />
                    </div>
                    <div className="hexagon-large" style={{animationDelay: '0.4s'}}>
                      <img src="/hero-6.png" alt="Cloud Architecture" />
                    </div>
                    <div className="hexagon-large" style={{animationDelay: '0.5s'}}>
                      <img src="/hero-4.png" alt="Chatbot Solutions" />
                    </div>
                  </div>
                  {/* Row 3 - 3 hexagons */}
                  <div className="hexagon-row hexagon-row-offset">
                    <div className="hexagon-large" style={{animationDelay: '0.6s'}}>
                      <img src="/hero-5.png" alt="Tech Solutions" />
                    </div>
                    <div className="hexagon-large" style={{animationDelay: '0.7s'}}>
                      <img src="/hero-7.png" alt="ERP Dashboard" />
                    </div>
                    <div className="hexagon-large" style={{animationDelay: '0.8s'}}>
                      <img src="/hero-8.png" alt="Development Workspace" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6 bg-transparent">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Services</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">Comprehensive technology solutions to drive your business forward</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card key={service.id} className="group hover:shadow-xl hover:shadow-[#2563EB]/20 transition-all duration-300 hover:-translate-y-2 border-2 border-[#38BDF8]/20 hover:border-[#38BDF8] bg-gradient-to-br from-[#1a2332] to-[#0f1419] overflow-hidden" style={{animationDelay: `${index * 100}ms`}}>
                {/* Service Image */}
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 h-12 w-12 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                    <span className="text-white text-2xl">{getIcon(service.icon)}</span>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl text-white">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-300">{service.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-6 md:py-8 px-4 md:px-6 bg-transparent">
        <div className="container mx-auto">
          <div className="text-center mb-4 md:mb-6">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-1">Why Choose Us</h2>
            <p className="text-sm md:text-base text-gray-300">What makes us the right partner for your digital transformation</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">
            {whyChooseUs.map((item, index) => (
              <div key={item.id} className="text-center p-3 md:p-4 rounded-xl bg-gradient-to-br from-white/5 to-white/10 hover:from-white/10 hover:to-white/15 border border-[#38BDF8]/20 hover:border-[#38BDF8]/50 transition-all duration-300">
                <div className={`h-10 w-10 md:h-12 md:w-12 rounded-lg md:rounded-xl bg-gradient-to-br ${getGradientColor(index)} flex items-center justify-center mx-auto mb-2 md:mb-3 shadow-lg`}>
                  <span className="text-white text-base md:text-lg">{getIcon(item.icon)}</span>
                </div>
                <h3 className="font-semibold text-xs md:text-sm text-white leading-snug">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-20 px-6 bg-transparent">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Industries We Serve</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">Delivering tailored solutions across diverse sectors</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {industries.map((industry) =>
            <Card key={industry.id} className="group hover:shadow-lg hover:shadow-[#2563EB]/20 transition-all duration-300 cursor-pointer hover:scale-105 bg-gradient-to-br from-[#1a2332] to-[#0f1419] border-[#38BDF8]/20">
                <CardHeader className="text-center">
                  <div className="h-20 w-20 rounded-full bg-gradient-to-br from-[#2563EB] to-[#38BDF8] flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform">
                    <span className="text-white text-3xl">{getIcon(industry.icon)}</span>
                  </div>
                  <CardTitle className="text-xl text-white">{industry.name}</CardTitle>
                </CardHeader>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-6 bg-transparent overflow-hidden">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Process</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">A proven methodology for delivering exceptional results</p>
          </div>
          <div className="relative max-w-7xl mx-auto">
            {/* Mobile: vertical timeline */}
            <div className="md:hidden space-y-6">
              {processSteps.map((step) => (
                <div key={step.id} className="flex gap-4 items-start">
                  <div className="h-14 w-14 flex-shrink-0 rounded-full bg-gradient-to-br from-[#2563EB] to-[#38BDF8] flex items-center justify-center text-white text-xl font-bold border-4 border-white shadow-xl">
                    {step.id}
                  </div>
                  <div className="pt-1">
                    <h3 className="font-semibold text-base text-white mb-1">{step.title}</h3>
                    <p className="text-sm text-gray-300">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: horizontal timeline */}
            <div className="hidden md:block">
            {/* Circles row */}
            <div className="flex items-center justify-center gap-8 mb-8">
              {processSteps.map((step, index) => (
                <React.Fragment key={step.id}>
                  {/* Circle */}
                  <div className="flex flex-col items-center">
                    <div className="h-20 w-20 rounded-full bg-gradient-to-br from-[#2563EB] to-[#38BDF8] flex items-center justify-center text-white text-2xl font-bold border-4 border-white shadow-xl animate-pulse-slow">
                      {step.id}
                    </div>
                  </div>
                  
                  {/* Arrow */}
                  {index < processSteps.length - 1 && (
                    <div className="flex items-center justify-center">
                      <svg width="60" height="24" viewBox="0 0 60 24" className="animate-arrow-flow">
                        <defs>
                          <linearGradient id={`gradient-${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#2563EB" />
                            <stop offset="100%" stopColor="#38BDF8" />
                          </linearGradient>
                        </defs>
                        <path 
                          d="M 5 12 L 45 12" 
                          stroke={`url(#gradient-${index})`}
                          strokeWidth="3" 
                          fill="none"
                          strokeLinecap="round"
                        />
                        <path 
                          d="M 38 6 L 50 12 L 38 18" 
                          stroke={`url(#gradient-${index})`}
                          strokeWidth="3" 
                          fill="none"
                          strokeLinejoin="round"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            
            {/* Text row */}
            <div className="grid grid-cols-6 gap-8">
              {processSteps.map((step) => (
                <div key={`text-${step.id}`} className="text-center">
                  <h3 className="font-semibold text-base text-white mb-2">{step.title}</h3>
                  <p className="text-xs text-gray-300">{step.description}</p>
                </div>
              ))}
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cloud Cost Savings Section */}
      <section className="py-12 px-6 bg-transparent text-white relative overflow-hidden">
        {/* Subtle grid pattern background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'linear-gradient(#38BDF8 1px, transparent 1px), linear-gradient(90deg, #38BDF8 1px, transparent 1px)', backgroundSize: '50px 50px'}}></div>
        </div>
        
        <div className="container mx-auto relative z-10 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Premium 3D Glass Cube ZX Logo */}
            <div className="flex items-center justify-center">
              <img 
                src="/zx-glass-cube-premium.png" 
                alt="ZenCodex Premium 3D Glass Cube Logo" 
                className="w-full max-w-lg h-auto drop-shadow-2xl"
              />
            </div>
            
            {/* Right Side - Content */}
            <div className="space-y-5">
              <h2 className="text-4xl font-bold leading-tight">
                Reduce Your Cloud Costs <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#38BDF8] to-[#2563EB]">By Up To 40%</span>
              </h2>
              
              <p className="text-base text-gray-300 leading-relaxed">
                Our cloud optimization experts identify waste, right-size resources and implement FinOps best practices to maximize your ROI.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <LucideIcons.Check className="h-5 w-5 text-[#38BDF8]" />
                  <span className="text-sm">AWS, Azure & GCP Experts</span>
                </div>
                <div className="flex items-center gap-3">
                  <LucideIcons.Check className="h-5 w-5 text-[#38BDF8]" />
                  <span className="text-sm">Cost Assessment in 48 Hours</span>
                </div>
                <div className="flex items-center gap-3">
                  <LucideIcons.Check className="h-5 w-5 text-[#38BDF8]" />
                  <span className="text-sm">No Upfront Charges</span>
                </div>
              </div>
              
              {/* Visualization Cards */}
              <div className="grid grid-cols-2 gap-4 mt-6">
                {/* Cost Overview Card - Left */}
                <div className="bg-gradient-to-br from-[#1a2332] to-[#0f1419] rounded-xl p-4 border border-[#38BDF8]/10">
                  <div className="mb-3">
                    <p className="text-gray-400 text-xs mb-1">Cost Overview</p>
                    <h3 className="text-2xl font-bold text-white">$28,460</h3>
                    <p className="text-xs text-gray-500">Total Monthly Cost</p>
                  </div>
                  
                  {/* Chart Visualization */}
                  <div className="relative h-32 flex items-center justify-center">
                    {/* Line chart background */}
                    <svg viewBox="0 0 100 60" className="absolute inset-0 w-full h-full opacity-50">
                      <path d="M 0,50 L 20,45 L 40,35 L 60,25 L 80,15 L 100,10" 
                            stroke="#2563EB" 
                            strokeWidth="1.5" 
                            fill="none"
                            className="animate-draw-line"/>
                    </svg>
                    
                    {/* Donut chart */}
                    <svg viewBox="0 0 100 100" className="w-20 h-20 relative z-10">
                      <circle cx="50" cy="50" r="35" fill="none" stroke="#1e293b" strokeWidth="8"/>
                      <circle 
                        cx="50" 
                        cy="50" 
                        r="35" 
                        fill="none" 
                        stroke="url(#costGradient2)" 
                        strokeWidth="8"
                        strokeDasharray="88 220"
                        strokeLinecap="round"
                        transform="rotate(-90 50 50)"
                      />
                      <defs>
                        <linearGradient id="costGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#10b981" />
                          <stop offset="100%" stopColor="#38BDF8" />
                        </linearGradient>
                      </defs>
                      <text x="50" y="50" textAnchor="middle" dy=".3em" className="text-xl font-bold fill-[#38BDF8]" fontSize="20">40%</text>
                    </svg>
                  </div>
                </div>
                
                {/* Top Archway Departments Card - Right */}
                <div className="bg-gradient-to-br from-[#1a2332] to-[#0f1419] rounded-xl p-4 border border-[#38BDF8]/10">
                  <h4 className="text-xs font-semibold mb-3 text-white">Top Archway Departments</h4>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">ML Research</span>
                      <span className="text-xs text-white font-semibold">$11,430</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Production</span>
                      <span className="text-xs text-white font-semibold">$8,120</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Right Sizing</span>
                      <span className="text-xs text-white font-semibold">$4,210</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Storage Optimization</span>
                      <span className="text-xs text-white font-semibold">$3,230</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Reserved Instances</span>
                      <span className="text-xs text-white font-semibold">$1,470</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 px-6 bg-transparent">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Portfolio</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">Recent projects that showcase our expertise</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolio.map((project) => (
              <Card key={project.id} className="group hover:shadow-xl hover:shadow-[#2563EB]/20 transition-all duration-300 overflow-hidden bg-gradient-to-br from-[#1a2332] to-[#0f1419] border-[#38BDF8]/20">
                <CardHeader className="p-0">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/30 to-transparent"></div>
                    <Badge className="absolute top-4 left-4 bg-[#2563EB]">{project.category}</Badge>
                  </div>
                  <div className="p-6 pb-0">
                    <CardTitle className="text-xl text-white">{project.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="px-6 pb-6">
                  <CardDescription className="text-gray-300 mb-4">{project.description}</CardDescription>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, index) => (
                      <Badge key={index} variant="outline" className="text-xs border-[#38BDF8]/30 text-gray-300">{tech}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-transparent text-white py-16 px-6">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <img src="/zencodex-complete-logo.png" alt="ZenCodex Technologies" className="h-32 md:h-40 mb-4" />
              <p className="text-gray-400 mb-4">Transforming businesses through innovative technology solutions.</p>
              <div className="flex gap-4">
                <a href="#" className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#2563EB] flex items-center justify-center transition-colors">
                  <LucideIcons.Linkedin className="h-5 w-5" />
                </a>
                <a href="#" className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#2563EB] flex items-center justify-center transition-colors">
                  <LucideIcons.Twitter className="h-5 w-5" />
                </a>
                <a href="#" className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#2563EB] flex items-center justify-center transition-colors">
                  <LucideIcons.Github className="h-5 w-5" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-[#38BDF8] transition-colors">ERP Solutions</a></li>
                <li><a href="#" className="hover:text-[#38BDF8] transition-colors">Cloud Architecture</a></li>
                <li><a href="#" className="hover:text-[#38BDF8] transition-colors">Web Development</a></li>
                <li><a href="#" className="hover:text-[#38BDF8] transition-colors">Mobile Apps</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-[#38BDF8] transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-[#38BDF8] transition-colors">Portfolio</a></li>
                <li><a href="#" className="hover:text-[#38BDF8] transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-[#38BDF8] transition-colors">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Contact</h4>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-start gap-2">
                  <LucideIcons.Mail className="h-5 w-5 mt-0.5 text-[#38BDF8]" />
                  <span>support@zencodex.ai.com</span>
                </li>
                <li className="flex items-start gap-2">
                  <LucideIcons.Phone className="h-5 w-5 mt-0.5 text-[#38BDF8]" />
                  <span>+91 7397505537</span>
                </li>
                {/* <li className="flex items-start gap-2">
                  <LucideIcons.MapPin className="h-5 w-5 mt-0.5 text-[#38BDF8]" />
                  <span>123 Tech Street, Silicon Valley, CA 94000</span>
                </li> */}
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 ZenCodex Technologies. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating Contact Button */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogTrigger asChild>
          <button className="fixed bottom-8 right-8 h-16 w-16 rounded-full bg-gradient-to-br from-[#2563EB] to-[#38BDF8] shadow-2xl hover:shadow-[#2563EB]/50 flex items-center justify-center text-white z-50 hover:scale-110 transition-all duration-300 animate-pulse-slow">
            <LucideIcons.MessageCircle className="h-7 w-7" />
          </button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[600px] bg-gradient-to-br from-[#0F172A] to-[#1e293b] text-white border-[#38BDF8]/30 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">Let's Build Something Amazing Together</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Company</label>
                <Input
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                  placeholder="Company name"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                  placeholder="your.email@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Phone</label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Service Required</label>
              <Select onValueChange={(value) => setFormData({...formData, service: value})}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.id} value={service.title}>{service.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Project Description</label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                placeholder="Tell us about your project..."
              />
            </div>
            <Button type="submit" size="lg" disabled={isSubmitting} className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white py-5 text-base disabled:opacity-50">
              {isSubmitting ? (
                <>
                  <LucideIcons.Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <LucideIcons.Send className="mr-2 h-5 w-5" />
                  Send Message
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>);

};

export default Home;